import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getTimer, subscribeToTimer, updateTimer, getSocket } from '../services/vpsApi';
import { getCompany } from '../services/vpsCompanies';
import { getGlobalSettings } from '../services/vpsGlobalSettings';
import { useLanguage } from '../contexts/LanguageContext';
import './TimerEmbed.css';

// Default sound settings
const DEFAULT_SOUND_SETTINGS = {
  enabled: true,
  volume: 60,
  beepCount: 3,
  pitch: 'medium',
  customSoundUrl: ''
};

// Pitch to frequency mapping
const PITCH_FREQUENCIES = {
  low: { base: 440, alt: 523 },     // A4, C5
  medium: { base: 880, alt: 1046 }, // A5, C6
  high: { base: 1320, alt: 1568 }   // E6, G6
};

function TimerEmbed({ fullVersion = false, fullVersion2 = false, displayVersion = false, timer: timerProp, embedded = false }) {
  const { timerId } = useParams();
  const { t, changeLanguage, availableLanguages } = useLanguage();
  const [timer, setTimer] = useState(timerProp || null);
  const [loading, setLoading] = useState(!timerProp);
  const [error, setError] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [segmentElapsed, setSegmentElapsed] = useState(0);
  const [prizePageIndex, setPrizePageIndex] = useState(0);
  const [companySettings, setCompanySettings] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);

  // Track previous segment index for change detection
  const prevSegmentIndexRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const customAudioRef = useRef(null);

  // Get effective sound settings (timer > company > global > default)
  const soundSettings = useMemo(() => {
    // If timer has custom settings and not using company settings
    if (timer?.soundSettings && !timer.soundSettings.useCompanySettings) {
      return { ...DEFAULT_SOUND_SETTINGS, ...timer.soundSettings };
    }
    // Use company settings if available
    if (companySettings?.soundSettings) {
      return { ...DEFAULT_SOUND_SETTINGS, ...companySettings.soundSettings };
    }
    // Use global settings if available
    if (globalSettings?.soundSettings) {
      return { ...DEFAULT_SOUND_SETTINGS, ...globalSettings.soundSettings };
    }
    // Fall back to hardcoded default
    return DEFAULT_SOUND_SETTINGS;
  }, [timer?.soundSettings, companySettings?.soundSettings, globalSettings?.soundSettings]);

  // Fetch global settings on mount
  useEffect(() => {
    const fetchGlobalSettings = async () => {
      try {
        const settings = await getGlobalSettings();
        setGlobalSettings(settings);
        console.log('🌐 Global settings loaded:', settings?.soundSettings);
      } catch (err) {
        console.error('Failed to fetch global settings:', err);
      }
    };
    fetchGlobalSettings();
  }, []);

  // Fetch company settings when timer has companyId
  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (timer?.companyId) {
        try {
          const company = await getCompany(timer.companyId);
          setCompanySettings(company);
          console.log('🏢 Company settings loaded:', company?.soundSettings);
        } catch (err) {
          console.error('Failed to fetch company settings:', err);
        }
      }
    };
    fetchCompanySettings();
  }, [timer?.companyId]);

  // Initialize AudioContext on first user interaction
  const initAudioContext = useCallback(() => {
    if (audioUnlockedRef.current) return;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        console.log('🔊 AudioContext created');
      }

      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().then(() => {
          console.log('🔊 AudioContext resumed');
          audioUnlockedRef.current = true;
        });
      } else {
        audioUnlockedRef.current = true;
      }
    } catch (err) {
      console.error('Failed to init AudioContext:', err);
    }
  }, []);

  // Setup event listener to unlock audio on first interaction
  useEffect(() => {
    const unlockAudio = () => {
      initAudioContext();
      // Remove listeners after first interaction
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    document.addEventListener('keydown', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, [initAudioContext]);

  // Play custom sound from URL
  const playCustomSound = useCallback((url, volume) => {
    try {
      // Stop previous custom audio if playing
      if (customAudioRef.current) {
        customAudioRef.current.pause();
        customAudioRef.current = null;
      }

      const audio = new Audio(url);
      audio.volume = volume / 100;
      customAudioRef.current = audio;

      audio.play().then(() => {
        console.log('🔊 Custom sound played:', url);
      }).catch(err => {
        console.error('Failed to play custom sound:', err);
        // Fall back to beep
        playBeepSound();
      });
    } catch (err) {
      console.error('Failed to load custom sound:', err);
      playBeepSound();
    }
  }, []);

  // Play beep sound with current settings
  const playBeepSound = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;

      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          playBeepWithSettings(audioContext);
        });
        return;
      }

      playBeepWithSettings(audioContext);
    } catch (err) {
      console.error('Failed to play beep:', err);
    }
  }, [soundSettings]);

  // Helper function to play beep with current settings
  const playBeepWithSettings = (audioContext) => {
    try {
      const { volume, beepCount, pitch } = soundSettings;
      const frequencies = PITCH_FREQUENCIES[pitch] || PITCH_FREQUENCIES.medium;
      const normalizedVolume = (volume / 100) * 0.8; // Max 0.8 to prevent distortion

      const currentTime = audioContext.currentTime;

      for (let i = 0; i < beepCount; i++) {
        const startTime = currentTime + i * 0.25;

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(
          i % 2 === 0 ? frequencies.base : frequencies.alt,
          startTime
        );

        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(normalizedVolume, startTime + 0.02);
        gainNode.gain.setValueAtTime(normalizedVolume, startTime + 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.18);

        oscillator.start(startTime);
        oscillator.stop(startTime + 0.2);
      }

      console.log(`🔊 Beep played: ${beepCount} beeps, ${pitch} pitch, ${volume}% volume`);
    } catch (err) {
      console.error('Failed to play beep:', err);
    }
  };

  // Play segment change notification sound
  const playSegmentChangeSound = useCallback(() => {
    // Check if sound is enabled
    if (!soundSettings.enabled) {
      console.log('🔇 Sound disabled, skipping');
      return;
    }

    // If custom sound URL is set, use it
    if (soundSettings.customSoundUrl) {
      playCustomSound(soundSettings.customSoundUrl, soundSettings.volume);
    } else {
      playBeepSound();
    }
  }, [soundSettings, playCustomSound, playBeepSound]);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(err => {
          console.error('Failed to close AudioContext:', err);
        });
      }
    };
  }, []);

  // Auto switch language based on timer.language
  useEffect(() => {
    if (timer?.language && availableLanguages.length > 0) {
      const lang = availableLanguages.find(l => l.code === timer.language || l.name === timer.language);
      if (lang) {
        changeLanguage(lang.id);
      }
    }
  }, [timer?.language, availableLanguages, changeLanguage]);

  // Prize pool pagination - auto flip pages
  useEffect(() => {
    if (!timer?.prizePool || timer.prizePool.length === 0) return;

    const itemsPerPage = 5; // 每頁顯示5個獎金項目
    const totalPages = Math.ceil(timer.prizePool.length / itemsPerPage);

    if (totalPages <= 1) return; // 只有一頁不需要翻頁

    const flipInterval = (timer.prizeFlipSpeed || 3) * 1000; // 預設3秒翻頁，可從 timer 設定調整

    const interval = setInterval(() => {
      setPrizePageIndex((prevIndex) => (prevIndex + 1) % totalPages);
    }, flipInterval);

    return () => clearInterval(interval);
  }, [timer?.prizePool, timer?.prizeFlipSpeed]);

  // Check if bubble time is active
  const isBubbleTimeActive = () => {
    if (!timer) return false;

    const raceCategory = timer.raceCategory;
    const tableCount = parseInt(timer.tableCount);
    const raceStatus = timer.raceStatus;

    // Bubble time conditions:
    // 1. 賽事類別 = 錦標賽
    // 2. 賽事狀態 = 泡泡時間
    // 3. 桌數 > 1
    return raceCategory === '錦標賽' &&
           raceStatus === '泡泡時間' &&
           !isNaN(tableCount) &&
           tableCount > 1;
  };

  // Update timer when timerProp changes (for embedded mode)
  useEffect(() => {
    if (timerProp) {
      setTimer(timerProp);
      setLoading(false);
    }
  }, [timerProp]);

  // Listen for force refresh events
  useEffect(() => {
    if (embedded || !timerId) return () => {};

    const socket = getSocket();

    const handleForceRefresh = (data) => {
      console.log('🔄 Force refresh event received:', data);
      if (data.timerId === timerId) {
        console.log('🔄 Reloading page due to force refresh');
        window.location.reload();
      }
    };

    socket.on('timer:force-refresh', handleForceRefresh);

    return () => {
      socket.off('timer:force-refresh', handleForceRefresh);
    };
  }, [timerId, embedded]);

  useEffect(() => {
    // Skip subscription if timer is passed as prop (embedded mode)
    if (embedded || !timerId) return () => {};

    // Subscribe to real-time updates
    const unsubscribe = subscribeToTimer(timerId, (timerData) => {
      // 如果是泡泡時間但沒有 bubblePauseStartTime，立即設定它（不使用 await）
      const raceCategory = timerData.raceCategory;
      const tableCount = parseInt(timerData.tableCount);
      const raceStatus = timerData.raceStatus;
      const currentlyInBubbleTime = raceCategory === '錦標賽' &&
                                     raceStatus === '泡泡時間' &&
                                     !isNaN(tableCount) &&
                                     tableCount > 1;

      if (currentlyInBubbleTime && !timerData.bubblePauseStartTime && timerData.status === 'active') {
        const now = new Date().toISOString();
        console.log('Initializing bubblePauseStartTime on load:', now);
        updateTimer(timerId, {
          bubblePauseStartTime: now,
          bubblePausedSeconds: timerData.bubblePausedSeconds || 0
        }).then(() => {
          // 更新 timerData 以便後續計算使用
          timerData.bubblePauseStartTime = now;
        }).catch(err => {
          console.error('Failed to initialize bubble pause start:', err);
        });
      }

      setTimer(timerData);
      setLoading(false);

      // Calculate elapsed time if timer is active or paused
      if ((timerData.status === 'active' || timerData.status === 'paused') && timerData.startTime) {
        const startTime = new Date(timerData.startTime);
        const now = new Date();
        let totalElapsed = Math.floor((now - startTime) / 1000);

        // 如果計時器是暫停狀態，使用 pausedElapsed
        if (timerData.status === 'paused' && timerData.pausedElapsed !== undefined) {
          totalElapsed = timerData.pausedElapsed;
        } else {
          // 如果有泡泡時間暫停資訊，扣除暫停的時間
          let totalPausedSeconds = timerData.bubblePausedSeconds || 0;

          if (currentlyInBubbleTime && timerData.bubblePauseStartTime) {
            const pauseStart = new Date(timerData.bubblePauseStartTime);
            const currentPauseDuration = Math.floor((now - pauseStart) / 1000);
            totalPausedSeconds += currentPauseDuration;
          }

          totalElapsed -= totalPausedSeconds;
        }

        // 確保 totalElapsed 不會是負數
        if (totalElapsed < 0) {
          console.warn('Negative totalElapsed detected:', totalElapsed, 'Setting to 0');
          totalElapsed = 0;
        }

        console.log('Timer refresh - totalElapsed:', totalElapsed, 'status:', timerData.status);

        setElapsedSeconds(totalElapsed);

        // 計算當前區段
        if (timerData.segments && timerData.segments.length > 0) {
          calculateCurrentSegment(timerData.segments, totalElapsed);
        }
      }
    });

    return () => unsubscribe();
  }, [timerId, embedded]);

  // 計算當前區段和區段內已過時間
  const calculateCurrentSegment = async (segments, totalElapsed) => {
    console.log('calculateCurrentSegment called with totalElapsed:', totalElapsed, 'segments count:', segments.length);
    let cumulativeTime = 0;
    let newSegmentIndex = null;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const segmentStart = cumulativeTime;
      const segmentEnd = cumulativeTime + segment.duration;

      console.log(`Checking segment ${i}: ${segment.name}, start: ${segmentStart}, end: ${segmentEnd}, totalElapsed: ${totalElapsed}`);

      if (totalElapsed >= segmentStart && totalElapsed < segmentEnd) {
        // 當前在這個區段
        newSegmentIndex = i;
        console.log(`Found current segment: ${i} (${segment.name})`);
        setCurrentSegment({
          ...segment,
          index: i,
          remaining: segmentEnd - totalElapsed
        });
        setSegmentElapsed(totalElapsed - segmentStart);

        // Play sound when segment changes (not on initial load)
        if (prevSegmentIndexRef.current !== null && prevSegmentIndexRef.current !== i) {
          console.log(`🔔 Segment changed from ${prevSegmentIndexRef.current} to ${i}, playing sound...`);
          console.log(`🔔 Previous segment: ${timer.segments[prevSegmentIndexRef.current]?.name}, New segment: ${segment.name}`);
          playSegmentChangeSound();
        } else if (prevSegmentIndexRef.current === null) {
          console.log(`🔔 Initial segment set to ${i} (${segment.name}), no sound on initial load`);
        }
        prevSegmentIndexRef.current = i;

        // Update currentSegmentIndex in Firestore if changed
        if (timer && timer.currentSegmentIndex !== i && timerId && !embedded) {
          try {
            await updateTimer(timerId, { currentSegmentIndex: i });
            console.log(`Updated currentSegmentIndex to ${i}`);

            // Check if this segment has "結束" tag - auto complete timer
            const segmentTags = segment.tag || segment.tags || '';
            if (segmentTags.includes('結束') || segmentTags.toLowerCase().includes('end')) {
              console.log('Reached end segment, auto-completing timer...');
              await updateTimer(timerId, {
                status: 'completed',
                endTime: new Date().toISOString()
              });
            }
          } catch (err) {
            console.error('Failed to update currentSegmentIndex:', err);
          }
        }

        return;
      }

      cumulativeTime = segmentEnd;
    }

    console.log('No matching segment found, showing last segment');

    // 如果已經超過所有區段，顯示最後一個區段為完成狀態
    if (segments.length > 0) {
      const lastIndex = segments.length - 1;
      setCurrentSegment({
        ...segments[lastIndex],
        index: lastIndex,
        remaining: 0
      });
      setSegmentElapsed(segments[lastIndex].duration);

      // Update to last segment if needed
      if (timer && timer.currentSegmentIndex !== lastIndex && timerId && !embedded) {
        try {
          await updateTimer(timerId, { currentSegmentIndex: lastIndex });
          console.log(`Updated currentSegmentIndex to ${lastIndex} (last segment)`);
        } catch (err) {
          console.error('Failed to update currentSegmentIndex:', err);
        }
      }
    }
  };

  // Track bubble time pause
  useEffect(() => {
    if (!timer || !timerId || embedded) return;

    const bubbleActive = isBubbleTimeActive();

    // 進入泡泡時間：記錄開始時間
    if (bubbleActive && !timer.bubblePauseStartTime) {
      const now = new Date().toISOString();
      console.log('Setting bubblePauseStartTime:', now);
      updateTimer(timerId, {
        bubblePauseStartTime: now,
        bubblePausedSeconds: timer.bubblePausedSeconds || 0
      }).catch(err => console.error('Failed to set bubble pause start:', err));
    }
    // 離開泡泡時間：累積暫停時間
    else if (!bubbleActive && timer.bubblePauseStartTime) {
      const pauseStart = new Date(timer.bubblePauseStartTime);
      const now = new Date();
      const pauseDuration = Math.floor((now - pauseStart) / 1000);
      const totalPausedSeconds = (timer.bubblePausedSeconds || 0) + pauseDuration;

      console.log('Ending bubble time, total paused:', totalPausedSeconds);
      updateTimer(timerId, {
        bubblePauseStartTime: null,
        bubblePausedSeconds: totalPausedSeconds
      }).catch(err => console.error('Failed to update bubble paused seconds:', err));
    }
  }, [timer?.raceStatus, timer?.raceCategory, timer?.tableCount, timerId, embedded]);

  // Update elapsed time every second for active timers (ONLY if not paused and not in bubble time)
  useEffect(() => {
    // IMPORTANT: Only run when status is 'active' AND not in bubble time AND not paused
    // This prevents time jumping when paused
    if (timer?.status === 'active' && !isBubbleTimeActive()) {
      const interval = setInterval(() => {
        setElapsedSeconds(prev => {
          const newElapsed = prev + 1;

          // Recalculate current segment
          if (timer.segments && timer.segments.length > 0) {
            calculateCurrentSegment(timer.segments, newElapsed);
          }

          return newElapsed;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
    // If paused, do NOT create interval - this prevents time jumping
  }, [timer?.status, timer?.segments, timer?.raceCategory, timer?.raceStatus, timer?.tableCount, timerId]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const getStatusText = (status) => {
    const statusMap = {
      active: t('status.active', '進行中'),
      paused: t('status.paused', '已暫停'),
      completed: t('status.completed', '已完成'),
      pending: t('status.pending', '待開始'),
      stopped: t('status.stopped', '已停止')
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return <div className="embed-loading">{t('message.loading', '載入中...')}</div>;
  }

  if (error || !timer) {
    return <div className="embed-error">{t('message.error', '錯誤')}：無法載入計時器</div>;
  }

  const progress = currentSegment && currentSegment.duration
    ? (segmentElapsed / currentSegment.duration) * 100
    : 0;

  // Get next segment
  const nextSegment = currentSegment && timer.segments && currentSegment.index < timer.segments.length - 1
    ? timer.segments[currentSegment.index + 1]
    : null;

  // Calculate average chip stack
  const calculateAverageChips = () => {
    const totalChips = parseInt(timer.totalChips) || 0;
    const remainingPlayers = parseInt(timer.remainingPlayers) || 0;

    if (totalChips === 0 || remainingPlayers === 0) return null;

    const avgChips = Math.floor(totalChips / remainingPlayers);
    const bigBlind = parseInt(currentSegment?.bigBlind) || 0;
    const avgBB = bigBlind > 0 ? (avgChips / bigBlind) : 0;

    // Format avgBB: remove decimal if it's a whole number
    const formattedAvgBB = avgBB % 1 === 0 ? Math.floor(avgBB) : avgBB.toFixed(1);

    return {
      avgChips,
      avgBB: formattedAvgBB
    };
  };

  const averageChips = calculateAverageChips();

  // Calculate time to next break
  const calculateTimeToBreak = () => {
    if (!timer.segments || !currentSegment) return null;

    let timeToBreak = currentSegment.remaining || 0;
    let foundBreak = false;

    // Search for next break segment
    for (let i = currentSegment.index + 1; i < timer.segments.length; i++) {
      const segment = timer.segments[i];

      // Check if segment name contains "休息" or "break" (case insensitive)
      if (segment.name && (
        segment.name.includes('休息') ||
        segment.name.toLowerCase().includes('break')
      )) {
        foundBreak = true;
        break;
      }

      timeToBreak += segment.duration;
    }

    return foundBreak ? timeToBreak : null;
  };

  const timeToBreak = calculateTimeToBreak();

  // Display Version - 顯示器版本 (垂直佈局)
  if (displayVersion) {
    const styles = timer.styles?.simple || timer.styles || {};

    const getFontSizeClass = (size) => {
      const sizeMap = {
        'xs': 'font-xs', 'sm': 'font-sm', 'md': 'font-md', 'lg': 'font-lg',
        'xl': 'font-xl', '2xl': 'font-2xl', '3xl': 'font-3xl', '4xl': 'font-4xl',
        '5xl': 'font-5xl', '6xl': 'font-6xl'
      };
      return sizeMap[size] || 'font-md';
    };

    const getBlockStyle = (blockStyles) => {
      if (!blockStyles) return {};
      const hexToRgba = (hex, opacity) => {
        if (!hex) return null;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const alpha = (opacity || 100) / 100;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      const style = {};
      if (blockStyles.color) style.color = blockStyles.color;
      if (blockStyles.borderColor) {
        style.border = `2px solid ${hexToRgba(blockStyles.borderColor, blockStyles.borderOpacity)}`;
      }
      if (blockStyles.bgColor) {
        style.backgroundColor = hexToRgba(blockStyles.bgColor, blockStyles.bgOpacity);
      }
      if (blockStyles.blur && blockStyles.blur > 0) {
        style.backdropFilter = `blur(${blockStyles.blur}px)`;
        style.WebkitBackdropFilter = `blur(${blockStyles.blur}px)`;
      }
      return style;
    };

    return (
      <div className="timer-display-wrapper">
        <div className="timer-display-container">
          {/* 比賽名稱 + LV 標籤 */}
          <div className="display-header">
            <div
              className={`display-race-name ${getFontSizeClass(styles.levelName?.fontSize)}`}
              style={getBlockStyle(styles.levelName)}
            >
              {currentSegment ? currentSegment.name : '待開始'}
            </div>
            <div className="display-level-badge">LV {currentSegment ? currentSegment.level || 1 : 1}</div>
          </div>

          {/* 倒數計時 */}
          <div
            className={`display-countdown ${getFontSizeClass(styles.countdown?.fontSize)}`}
            style={getBlockStyle(styles.countdown)}
          >
            {currentSegment ? formatTime(currentSegment.remaining) : '00:00'}
          </div>

          {/* 下次休息 */}
          <div className="display-break-time">
            <span className="display-break-label">下次休息</span>
            <span className="display-break-value">
              {timeToBreak !== null ? formatTime(timeToBreak) : '-'}
            </span>
          </div>

          {/* 目前盲注 */}
          <div
            className={`display-blind-box current ${getFontSizeClass(styles.currentBlind?.fontSize)}`}
            style={getBlockStyle(styles.currentBlind)}
          >
            {currentSegment ? (
              `目前盲注 ${currentSegment.smallBlind || '-'} / ${currentSegment.bigBlind || '-'}${currentSegment.ante ? ' (' + currentSegment.ante + ')' : ''}`
            ) : '待開始'}
          </div>

          {/* 下個盲注 */}
          <div
            className={`display-blind-box next ${getFontSizeClass(styles.nextBlind?.fontSize)}`}
            style={getBlockStyle(styles.nextBlind)}
          >
            {nextSegment ? (
              `下個盲注 ${nextSegment.smallBlind || '-'} / ${nextSegment.bigBlind || '-'}${nextSegment.ante ? ' (' + nextSegment.ante + ')' : ''}`
            ) : '無'}
          </div>
        </div>
      </div>
    );
  }

  if (!fullVersion && !fullVersion2) {
    // Simple Version - 簡易版顯示 (16:9 固定比例)
    const simpleStyle = {
      ...(timer.simpleBackgroundImage ? {
        backgroundImage: `url(${timer.simpleBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}),
      // Apply border style
      border: timer.borderStyle === 'solid'
        ? `4px solid ${timer.borderColor || '#ffffff'}`
        : 'none'
    };

    // 取得各區塊的樣式設定（簡易版）
    const styles = timer.styles?.simple || timer.styles || {};

    // 各區塊的字體大小 class
    const getFontSizeClass = (size) => {
      // 如果是數字，返回空字串（使用 inline style）
      const isNumber = typeof size === 'number' ||
                      (typeof size === 'string' && /^\d+(\.\d+)?$/.test(String(size).trim()));
      if (isNumber) {
        return '';
      }
      const sizeMap = {
        'xs': 'font-xs',
        'sm': 'font-sm',
        'md': 'font-md',
        'lg': 'font-lg',
        'xl': 'font-xl',
        '2xl': 'font-2xl',
        '3xl': 'font-3xl',
        '4xl': 'font-4xl',
        '5xl': 'font-5xl',
        '6xl': 'font-6xl'
      };
      return sizeMap[size] || 'font-md';
    };

    // 生成區塊樣式（邊框、底色、透明度、模糊度）+ 字體顏色 + 字體大小
    const getBlockStyle = (blockStyles) => {
      if (!blockStyles) return {};

      const hexToRgba = (hex, opacity) => {
        if (!hex) return null;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const alpha = (opacity || 100) / 100;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const style = {};

      // 字體大小（如果是數字，直接設定為 px）
      if (blockStyles.fontSize !== undefined && blockStyles.fontSize !== null) {
        const fontSize = blockStyles.fontSize;
        // 檢查是否為純數字（數字型別或純數字字串）
        const isNumber = typeof fontSize === 'number' ||
                        (typeof fontSize === 'string' && /^\d+(\.\d+)?$/.test(fontSize.trim()));
        if (isNumber) {
          style.fontSize = `${fontSize}px`;
        }
        // 如果是字串類別（xs, sm, md等），由 className 處理，不設定 inline style
      }

      // 字體顏色（優先套用，即使沒有區塊樣式也要套用）
      if (blockStyles.color) {
        style.color = blockStyles.color;
      }

      // 邊框顏色和透明度
      if (blockStyles.borderColor) {
        style.border = `2px solid ${hexToRgba(blockStyles.borderColor, blockStyles.borderOpacity)}`;
      }

      // 底色和透明度
      if (blockStyles.bgColor) {
        style.backgroundColor = hexToRgba(blockStyles.bgColor, blockStyles.bgOpacity);
      }

      // 模糊度（毛玻璃效果）
      if (blockStyles.blur && blockStyles.blur > 0) {
        style.backdropFilter = `blur(${blockStyles.blur}px)`;
        style.WebkitBackdropFilter = `blur(${blockStyles.blur}px)`;
      }

      return style;
    };

    // 泡泡時間橫幅樣式
    const bubbleBannerStyle = {};
    if (timer.bubbleTimeFontSize) {
      bubbleBannerStyle.fontSize = ''; // 使用 class
    }
    if (timer.bubbleTimeFontColor) {
      bubbleBannerStyle.color = timer.bubbleTimeFontColor;
    }
    if (timer.bubbleTimeBackgroundColor) {
      // 將 hex 轉為半透明
      const hexToRgba = (hex, alpha = 0.85) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      bubbleBannerStyle.background = `linear-gradient(135deg, ${hexToRgba(timer.bubbleTimeBackgroundColor, 0.85)} 0%, ${hexToRgba(timer.bubbleTimeBackgroundColor, 0.75)} 100%)`;
    }

    return (
      <div className="timer-embed-wrapper">
        <div className="timer-embed-container">
          <div className="aspect-ratio-box">
            <div className="timer-embed-simple" style={simpleStyle}>
              {/* Logo (如果有設定) */}
              {timer.simpleLogo && (
                <div className={`simple-logo ${timer.simpleLogoSize ? `size-${timer.simpleLogoSize}` : 'size-md'}`}>
                  <img src={timer.simpleLogo} alt="Logo" />
                </div>
              )}

              {/* 區間名稱 */}
              <div
                className={`simple-level-name ${getFontSizeClass(styles.levelName?.fontSize)}`}
                style={getBlockStyle(styles.levelName)}
              >
                {currentSegment ? currentSegment.name : '待開始'}
              </div>

              {/* 倒數 */}
              <div
                className={`simple-countdown ${getFontSizeClass(styles.countdown?.fontSize)}`}
                style={getBlockStyle(styles.countdown)}
              >
                {currentSegment ? formatTime(currentSegment.remaining) : '00:00'}
              </div>

              {/* 目前盲注 */}
              <div className="simple-blind-box current" style={getBlockStyle(styles.currentBlind)}>
                <div
                  className={`simple-blind-oneline ${getFontSizeClass(styles.currentBlind?.fontSize)}`}
                >
                  {currentSegment ? (
                    `${t('timer.simple.currentBlind', '目前盲注')} ${currentSegment.smallBlind || '-'} / ${currentSegment.bigBlind || '-'}${currentSegment.ante ? ' (' + currentSegment.ante + ')' : ''}`
                  ) : t('status.pending', '待開始')}
                </div>
              </div>

              {/* Bubble Time Banner - 在目前盲注和下個盲注之間 */}
              {isBubbleTimeActive() && (
                <div
                  className={`bubble-time-banner-inline ${timer.bubbleTimeFontSize ? getFontSizeClass(timer.bubbleTimeFontSize) : ''}`}
                  style={bubbleBannerStyle}
                >
                  <div className="bubble-time-text">
                    🫧 {t('control.bubbleTimeActive', '泡泡時間進行中')} 🫧
                  </div>
                </div>
              )}

              {/* 下個盲注 */}
              <div className="simple-blind-box next" style={getBlockStyle(styles.nextBlind)}>
                <div
                  className={`simple-blind-oneline ${getFontSizeClass(styles.nextBlind?.fontSize)}`}
                >
                  {nextSegment ? (
                    `${t('timer.simple.nextBlind', '下個盲注')} ${nextSegment.smallBlind || '-'} / ${nextSegment.bigBlind || '-'}${nextSegment.ante ? ' (' + nextSegment.ante + ')' : ''}`
                  ) : '無'}
                </div>
              </div>

              {/* 備註 (如果有設定) */}
              {timer.notes && (
                <div className="simple-notes">
                  {timer.notes}
                </div>
              )}

              {/* 底部資訊列 */}
              <div className="simple-bottom-info">
                <div className="simple-info-item" style={getBlockStyle(styles.bottomInfo)}>
                  <div
                    className={`simple-info-label ${getFontSizeClass(styles.bottomInfo?.fontSize)}`}
                  >
                    {t('timer.simple.timeToBreak', '多久下個休息')}
                  </div>
                  <div
                    className={`simple-info-value ${getFontSizeClass(styles.bottomInfo?.fontSize)}`}
                  >
                    {timeToBreak !== null ? formatTime(timeToBreak) : '-'}
                  </div>
                </div>
                <div className="simple-info-item" style={getBlockStyle(styles.bottomInfo)}>
                  <div
                    className={`simple-info-label ${getFontSizeClass(styles.bottomInfo?.fontSize)}`}
                  >
                    {t('timer.simple.avgChips', '均碼')}
                  </div>
                  <div
                    className={`simple-info-value ${getFontSizeClass(styles.bottomInfo?.fontSize)}`}
                  >
                    {averageChips ? (
                      <>
                        {averageChips.avgChips.toLocaleString()}
                        <span className="simple-info-sub"> ({averageChips.avgBB} {t('common.bb', 'BB')})</span>
                      </>
                    ) : '-'}
                  </div>
                </div>
                <div className="simple-info-item" style={getBlockStyle(styles.bottomInfo)}>
                  <div
                    className={`simple-info-label ${getFontSizeClass(styles.bottomInfo?.fontSize)}`}
                  >
                    {t('timer.simple.playerRank', '玩家名次')}
                  </div>
                  <div
                    className={`simple-info-value ${getFontSizeClass(styles.bottomInfo?.fontSize)}`}
                  >
                    {timer.remainingPlayers || '-'} / {timer.totalGroups || '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full Version 2 - Flutter Spec Based Layout (完整版二 - 參考 Flutter 規格)
  if (fullVersion2) {
    const fullStyles = timer.styles?.full || timer.styles || {};

    const displayStyle = {
      backgroundColor: '#0B0F14',
      ...(timer.fullBackgroundImage ? {
        backgroundImage: `url(${timer.fullBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay'
      } : {})
    };

    // Color scheme from Flutter spec
    const colors = {
      background: '#0B0F14',
      primaryBlue: '#7FB6FF',
      secondaryGreen: '#9FD66B',
      dangerRed: '#E05A5A',
      divider: 'rgba(255, 255, 255, 0.1)',
      textWhite: '#FFFFFF',
      textMuted: 'rgba(255, 255, 255, 0.7)'
    };

    // Get ordinal suffix for rankings
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    // 字體大小 class 函數
    const getFontSizeClass = (size) => {
      const isNumber = typeof size === 'number' ||
                      (typeof size === 'string' && /^\d+(\.\d+)?$/.test(String(size).trim()));
      if (isNumber) {
        return '';
      }
      const sizeMap = {
        'xs': 'font-xs', 'sm': 'font-sm', 'md': 'font-md', 'lg': 'font-lg',
        'xl': 'font-xl', '2xl': 'font-2xl', '3xl': 'font-3xl', '4xl': 'font-4xl',
        '5xl': 'font-5xl', '6xl': 'font-6xl'
      };
      return sizeMap[size] || '';
    };

    // 生成區塊樣式
    const getBlockStyle = (blockStyles, defaultColor = null) => {
      if (!blockStyles) return defaultColor ? { color: defaultColor } : {};

      const hexToRgba = (hex, opacity) => {
        if (!hex) return null;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const alpha = (opacity || 100) / 100;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const style = {};

      if (blockStyles.fontSize !== undefined && blockStyles.fontSize !== null) {
        const fontSize = blockStyles.fontSize;
        const isNumber = typeof fontSize === 'number' ||
                        (typeof fontSize === 'string' && /^\d+(\.\d+)?$/.test(fontSize.trim()));
        if (isNumber) {
          style.fontSize = `${fontSize}px`;
        }
      }

      if (blockStyles.color) {
        style.color = blockStyles.color;
      } else if (defaultColor) {
        style.color = defaultColor;
      }

      if (blockStyles.borderColor) {
        style.border = `2px solid ${hexToRgba(blockStyles.borderColor, blockStyles.borderOpacity)}`;
      }

      if (blockStyles.bgColor) {
        style.backgroundColor = hexToRgba(blockStyles.bgColor, blockStyles.bgOpacity);
      }

      if (blockStyles.blur && blockStyles.blur > 0) {
        style.backdropFilter = `blur(${blockStyles.blur}px)`;
        style.WebkitBackdropFilter = `blur(${blockStyles.blur}px)`;
      }

      return style;
    };

    return (
      <div className="timer-embed-wrapper full-version2-wrapper">
        <div className="timer-embed-container full-version2-container">
          <div className="aspect-ratio-box full-version2-box">
            <div className="fv2-layout" style={displayStyle}>
              {/* Header Row */}
              <div className="fv2-header">
                {/* Left: Logo */}
                <div className="fv2-header-left">
                  {timer.fullLogo ? (
                    <img src={timer.fullLogo} alt="Logo" className="fv2-logo" />
                  ) : (
                    <div className="fv2-logo-placeholder">LOGO</div>
                  )}
                </div>

                {/* Center: Event Name */}
                <div className="fv2-header-center">
                  <span
                    className={`fv2-event-name ${getFontSizeClass(fullStyles.timerName?.fontSize)}`}
                    style={getBlockStyle(fullStyles.timerName)}
                  >
                    {timer.name || 'Tournament'}
                  </span>
                </div>

                {/* Right: Level + Registration Status */}
                <div className="fv2-header-right">
                  <div className="fv2-level-badge">
                    LV {currentSegment?.level || (currentSegment?.index !== undefined ? currentSegment.index + 1 : 1)}
                  </div>
                  <div className="fv2-reg-status" style={{ color: colors.secondaryGreen }}>
                    {timer.raceStatus || t('timer.full.regOpen', '報名中')}
                  </div>
                </div>
              </div>

              {/* Main Content Row (3 columns) */}
              <div className="fv2-main">
                {/* Left Panel: Prize List (22%) */}
                <div className="fv2-left-panel">
                  <div
                    className={`fv2-panel-title ${getFontSizeClass(fullStyles.prizePoolTitle?.fontSize)}`}
                    style={getBlockStyle(fullStyles.prizePoolTitle, colors.primaryBlue)}
                  >
                    {t('timer.full.prizePool', '獎金')}
                  </div>
                  <div
                    className={`fv2-prize-list ${getFontSizeClass(fullStyles.prizePoolContent?.fontSize)}`}
                    style={getBlockStyle(fullStyles.prizePoolContent)}
                  >
                    {timer.showPrize && timer.prizePool && timer.prizePool.length > 0 ? (
                      (() => {
                        const itemsPerPage = 8;
                        const startIdx = prizePageIndex * itemsPerPage;
                        const endIdx = startIdx + itemsPerPage;
                        const currentPagePrizes = timer.prizePool.slice(startIdx, endIdx);

                        return currentPagePrizes.map((prize, idx) => {
                          const actualIndex = startIdx + idx;
                          return (
                            <div key={actualIndex} className="fv2-prize-row">
                              <span className="fv2-prize-rank" style={{ color: colors.textMuted }}>
                                {getOrdinal(parseInt(prize.rank) || actualIndex + 1)}
                              </span>
                              <span className="fv2-prize-amount" style={{ color: colors.primaryBlue }}>
                                ${(parseInt(prize.amount) || 0).toLocaleString()}
                              </span>
                            </div>
                          );
                        });
                      })()
                    ) : (
                      <div className="fv2-no-prize" style={{ color: colors.textMuted }}>
                        {timer.showPrize ? '未設定' : '獎金未公開'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Center Panel: Timer (46%) */}
                <div className="fv2-center-panel">
                  {/* Level Name */}
                  <div
                    className={`fv2-level-name ${getFontSizeClass(fullStyles.levelName?.fontSize)}`}
                    style={getBlockStyle(fullStyles.levelName, colors.primaryBlue)}
                  >
                    {currentSegment ? currentSegment.name : t('status.pending', '待開始')}
                  </div>

                  {/* Countdown Timer */}
                  <div
                    className={`fv2-countdown ${getFontSizeClass(fullStyles.countdown?.fontSize)}`}
                    style={getBlockStyle(fullStyles.countdown, colors.textWhite)}
                  >
                    {currentSegment ? formatTime(currentSegment.remaining) : '00:00'}
                  </div>

                  {/* Current Blind */}
                  <div
                    className={`fv2-blind-box fv2-current-blind ${getFontSizeClass(fullStyles.currentBlind?.fontSize)}`}
                    style={getBlockStyle(fullStyles.currentBlind)}
                  >
                    <div className="fv2-blind-label">{t('timer.full.currentBlind', '目前盲注')}</div>
                    <div className="fv2-blind-values">
                      <span className="fv2-blind-sb">{currentSegment?.smallBlind || '-'}</span>
                      <span className="fv2-blind-separator">/</span>
                      <span className="fv2-blind-bb">{currentSegment?.bigBlind || '-'}</span>
                      {currentSegment?.ante && currentSegment.ante !== '-' && currentSegment.ante !== '0' && (
                        <span className="fv2-blind-ante">({currentSegment.ante})</span>
                      )}
                    </div>
                  </div>

                  {/* Next Blind */}
                  <div
                    className={`fv2-blind-box fv2-next-blind ${getFontSizeClass(fullStyles.nextBlind?.fontSize)}`}
                    style={getBlockStyle(fullStyles.nextBlind, colors.textMuted)}
                  >
                    <div className="fv2-blind-label">{t('timer.full.nextBlind', '下個盲注')}</div>
                    <div className="fv2-blind-values">
                      {nextSegment ? (
                        <>
                          <span className="fv2-blind-sb">{nextSegment.smallBlind || '-'}</span>
                          <span className="fv2-blind-separator">/</span>
                          <span className="fv2-blind-bb">{nextSegment.bigBlind || '-'}</span>
                          {nextSegment?.ante && nextSegment.ante !== '-' && nextSegment.ante !== '0' && (
                            <span className="fv2-blind-ante">({nextSegment.ante})</span>
                          )}
                        </>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Panel: Stats (22%) */}
                <div className="fv2-right-panel">
                  {/* Next Break */}
                  <div
                    className={`fv2-stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`}
                    style={getBlockStyle(fullStyles.stats)}
                  >
                    <div className="fv2-stat-label">{t('timer.full.timeToBreak', '下次休息')}</div>
                    <div className="fv2-stat-value" style={{ color: colors.primaryBlue }}>
                      {timeToBreak !== null ? formatTime(timeToBreak) : '-'}
                    </div>
                  </div>

                  {/* Average Stack */}
                  <div
                    className={`fv2-stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`}
                    style={getBlockStyle(fullStyles.stats)}
                  >
                    <div className="fv2-stat-label">{t('timer.full.avgStack', '均碼')}</div>
                    <div className="fv2-stat-value" style={{ color: colors.secondaryGreen }}>
                      {averageChips ? averageChips.avgChips.toLocaleString() : '-'}
                    </div>
                    {averageChips && (
                      <div className="fv2-stat-sub" style={{ color: colors.textMuted }}>
                        ({averageChips.avgBB} BB)
                      </div>
                    )}
                  </div>

                  {/* Player Count */}
                  <div
                    className={`fv2-stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`}
                    style={getBlockStyle(fullStyles.stats)}
                  >
                    <div className="fv2-stat-label">{t('timer.full.playersCount', '剩餘人數')}</div>
                    <div className="fv2-stat-value" style={{ color: colors.textWhite }}>
                      {timer.remainingPlayers || '-'}
                      <span className="fv2-player-total" style={{ color: colors.textMuted }}>
                        / {timer.totalGroups || '-'}
                      </span>
                    </div>
                  </div>

                  {/* Total Chips */}
                  <div
                    className={`fv2-stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`}
                    style={getBlockStyle(fullStyles.stats)}
                  >
                    <div className="fv2-stat-label">{t('timer.full.totalChips', '總碼量')}</div>
                    <div className="fv2-stat-value" style={{ color: colors.textWhite }}>
                      {timer.totalChips ? parseInt(timer.totalChips).toLocaleString() : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom: Marquee Notice */}
              <div className="fv2-footer">
                <div
                  className={`fv2-marquee ${getFontSizeClass(fullStyles.marquee?.fontSize)}`}
                  style={getBlockStyle(fullStyles.marquee, colors.dangerRed)}
                >
                  <div className="fv2-marquee-content">
                    {timer.marqueeText || timer.notes || t('timer.full.marquee', '廣告跑馬燈')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Professional Tournament Display Layout (完整版 - 16:9 固定比例)
  if (fullVersion) {
    // 取得完整版樣式設定
    const fullStyles = timer.styles?.full || timer.styles || {};

    const displayStyle = {
      ...(timer.fullBackgroundImage ? {
        backgroundImage: `url(${timer.fullBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay'
      } : {}),
      // Apply border style
      border: timer.borderStyle === 'solid'
        ? `4px solid ${timer.borderColor || '#ffffff'}`
        : 'none'
    };

    // 字體大小 class 函數
    const getFontSizeClass = (size) => {
      // 如果是數字，返回空字串（使用 inline style）
      const isNumber = typeof size === 'number' ||
                      (typeof size === 'string' && /^\d+(\.\d+)?$/.test(String(size).trim()));
      if (isNumber) {
        return '';
      }
      const sizeMap = {
        'xs': 'font-xs',
        'sm': 'font-sm',
        'md': 'font-md',
        'lg': 'font-lg',
        'xl': 'font-xl',
        '2xl': 'font-2xl',
        '3xl': 'font-3xl',
        '4xl': 'font-4xl',
        '5xl': 'font-5xl',
        '6xl': 'font-6xl'
      };
      return sizeMap[size] || 'font-md';
    };

    // 生成區塊樣式（邊框、底色、透明度、模糊度）+ 字體顏色 + 字體大小 - 完整版
    const getBlockStyle = (blockStyles) => {
      if (!blockStyles) return {};

      const hexToRgba = (hex, opacity) => {
        if (!hex) return null;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const alpha = (opacity || 100) / 100;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const style = {};

      // 字體大小（如果是數字，直接設定為 px）
      if (blockStyles.fontSize !== undefined && blockStyles.fontSize !== null) {
        const fontSize = blockStyles.fontSize;
        // 檢查是否為純數字（數字型別或純數字字串）
        const isNumber = typeof fontSize === 'number' ||
                        (typeof fontSize === 'string' && /^\d+(\.\d+)?$/.test(fontSize.trim()));
        if (isNumber) {
          style.fontSize = `${fontSize}px`;
        }
        // 如果是字串類別（xs, sm, md等），由 className 處理，不設定 inline style
      }

      // 字體顏色（優先套用，即使沒有區塊樣式也要套用）
      if (blockStyles.color) {
        style.color = blockStyles.color;
      }

      // 邊框顏色和透明度
      if (blockStyles.borderColor) {
        style.border = `2px solid ${hexToRgba(blockStyles.borderColor, blockStyles.borderOpacity)}`;
      }

      // 底色和透明度
      if (blockStyles.bgColor) {
        style.backgroundColor = hexToRgba(blockStyles.bgColor, blockStyles.bgOpacity);
      }

      // 模糊度（毛玻璃效果）
      if (blockStyles.blur && blockStyles.blur > 0) {
        style.backdropFilter = `blur(${blockStyles.blur}px)`;
        style.WebkitBackdropFilter = `blur(${blockStyles.blur}px)`;
      }

      return style;
    };

    // Font size class (完整版未使用全域字體大小，保留空字串避免錯誤)
    const fontSizeClass = '';

    // 泡泡時間橫幅樣式
    const bubbleBannerStyle = {};
    if (timer.bubbleTimeFontSize) {
      bubbleBannerStyle.fontSize = ''; // 使用 class
    }
    if (timer.bubbleTimeFontColor) {
      bubbleBannerStyle.color = timer.bubbleTimeFontColor;
    }
    if (timer.bubbleTimeBackgroundColor) {
      // 將 hex 轉為半透明
      const hexToRgba = (hex, alpha = 0.85) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      bubbleBannerStyle.background = `linear-gradient(135deg, ${hexToRgba(timer.bubbleTimeBackgroundColor, 0.85)} 0%, ${hexToRgba(timer.bubbleTimeBackgroundColor, 0.75)} 100%)`;
    }

    return (
      <div className="timer-embed-wrapper full-version-wrapper">
        <div className="timer-embed-container full-version-container">
          <div className="aspect-ratio-box full-version-box">
            {/* Bubble Time Banner - 在 16:9 容器內 */}
            {isBubbleTimeActive() && (
              <div
                className={`bubble-time-banner ${timer.bubbleTimeFontSize ? getFontSizeClass(timer.bubbleTimeFontSize) : ''}`}
                style={bubbleBannerStyle}
              >
                <div className="bubble-time-text">
                  🫧 {t('control.bubbleTimeActive', '泡泡時間進行中')} 🫧
                </div>
              </div>
            )}

            <div
              className={`tournament-display ${fontSizeClass} ${isBubbleTimeActive() ? 'bubble-time-active' : ''}`}
              style={displayStyle}
            >
        {/* Left Column */}
        <div className="left-column">
          <div className={`logo-section ${timer.fullLogoSize ? `size-${timer.fullLogoSize}` : 'size-md'}`}>
            {timer.fullLogo ? (
              <img src={timer.fullLogo} alt="Logo" className="full-logo-img" />
            ) : (
              <div className="logo-placeholder">LOGO</div>
            )}
          </div>
          <div className="prize-section">
            <div
              className={`section-title ${getFontSizeClass(fullStyles.prizePoolTitle?.fontSize)}`}
              style={getBlockStyle(fullStyles.prizePoolTitle)}
            >
              {t('timer.full.prizePool', '名次獎金表')}
            </div>
            <div className="prize-content">
              {timer.showPrize && timer.prizePool && timer.prizePool.length > 0 ? (
                <div
                  className={`prize-list ${getFontSizeClass(fullStyles.prizePoolContent?.fontSize)}`}
                  style={getBlockStyle(fullStyles.prizePoolContent)}
                >
                  {(() => {
                    const itemsPerPage = 5;
                    const startIdx = prizePageIndex * itemsPerPage;
                    const endIdx = startIdx + itemsPerPage;
                    const currentPagePrizes = timer.prizePool.slice(startIdx, endIdx);

                    return currentPagePrizes.map((prize, idx) => {
                      const actualIndex = startIdx + idx;
                      return (
                        <div key={actualIndex} className="prize-item">
                          <span className="prize-rank">{prize.rank || (actualIndex + 1)}.</span>
                          <span className="prize-amount">{prize.amount || prize}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className="prize-placeholder">{timer.showPrize ? '未設定獎金' : '獎金未公開'}</div>
              )}
            </div>
          </div>
          {timer.showPrize && (
            <div className="current-prize">
              <div
                className={`section-title ${getFontSizeClass(fullStyles.currentPrizeTitle?.fontSize)}`}
                style={getBlockStyle(fullStyles.currentPrizeTitle)}
              >
                {t('timer.full.currentPrize', '目前獎金')}
              </div>
              <div
                className={`current-prize-amount ${getFontSizeClass(fullStyles.currentPrizeAmount?.fontSize)}`}
                style={getBlockStyle(fullStyles.currentPrizeAmount)}
              >
                {timer.currentPrize ? parseInt(timer.currentPrize).toLocaleString() : '-'}
              </div>
            </div>
          )}
          <div className="total-prize">
            <div
              className={`section-title ${getFontSizeClass(fullStyles.totalPrizeTitle?.fontSize)}`}
              style={getBlockStyle(fullStyles.totalPrizeTitle)}
            >
              {t('timer.full.totalChips', '總獎金')}
            </div>
            <div
              className={`total-prize-amount ${getFontSizeClass(fullStyles.totalPrizeAmount?.fontSize)}`}
              style={getBlockStyle(fullStyles.totalPrizeAmount)}
            >
              {timer.totalPrize ? parseInt(timer.totalPrize).toLocaleString() : '-'}
            </div>
          </div>
        </div>

        {/* Center Column */}
        <div className="center-column">
          <div className={`tournament-title ${getFontSizeClass(fullStyles.timerName?.fontSize)}`} style={getBlockStyle(fullStyles.timerName)}>{timer.name}</div>

          <div className="main-info-box">
            <div className={`level-title ${getFontSizeClass(fullStyles.levelName?.fontSize)}`} style={getBlockStyle(fullStyles.levelName)}>
              {currentSegment ? currentSegment.name : t('status.pending', '待開始')}
            </div>
            <div className={`countdown-timer ${getFontSizeClass(fullStyles.countdown?.fontSize)}`} style={getBlockStyle(fullStyles.countdown)}>
              {currentSegment ? formatTime(currentSegment.remaining) : '00:00'}
            </div>
          </div>

          <div className="break-info">
            {t('timer.full.timeToBreak', '距離下次休息時間')}
            {timeToBreak !== null && (
              <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px', color: '#ffd700' }}>
                {formatTime(timeToBreak)}
              </div>
            )}
          </div>

          <div className={`blinds-box current ${getFontSizeClass(fullStyles.currentBlind?.fontSize)}`} style={getBlockStyle(fullStyles.currentBlind)}>
            <div className="blinds-oneline">
              {currentSegment ? (
                `${t('timer.full.currentBlind', '目前級別')} 小盲: ${currentSegment.smallBlind || '-'} / 大盲: ${currentSegment.bigBlind || '-'} / ${t('common.ante', '前注')}: ${currentSegment.ante || '-'}`
              ) : '目前級別 -'}
            </div>
          </div>

          <div className={`blinds-box next ${getFontSizeClass(fullStyles.nextBlind?.fontSize)}`} style={getBlockStyle(fullStyles.nextBlind)}>
            <div className="blinds-oneline">
              {nextSegment ? (
                `${t('timer.full.nextBlind', '下個級別')} 小盲: ${nextSegment.smallBlind || '-'} / 大盲: ${nextSegment.bigBlind || '-'} / ${t('common.ante', '前注')}: ${nextSegment.ante || '-'}`
              ) : '下個級別 無'}
            </div>
          </div>

          <div className={`marquee-section ${getFontSizeClass(fullStyles.marquee?.fontSize)}`} style={getBlockStyle(fullStyles.marquee)}>
            <div className="marquee-content">
              {timer.marqueeText || timer.notes || t('timer.full.marquee', '廣告跑馬燈')}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="right-column">
          <div className={`stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`} style={getBlockStyle(fullStyles.stats)}>
            <div className="stat-label">{t('timer.full.avgStack', '均碼')} / {t('timer.full.avgStack', '均碼')}{t('common.bb', 'BB')}</div>
            <div className="stat-value">
              {averageChips ? (
                <>
                  {averageChips.avgChips.toLocaleString()}
                  <div style={{ fontSize: '0.6em', marginTop: '8px', color: 'inherit' }}>
                    {averageChips.avgBB} {t('common.bb', 'BB')}
                  </div>
                </>
              ) : '-'}
            </div>
          </div>
          <div className={`stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`} style={getBlockStyle(fullStyles.stats)}>
            <div className="stat-label">{t('timer.full.totalChips', '總碼量')}</div>
            <div className="stat-value">
              {timer.totalChips ? parseInt(timer.totalChips).toLocaleString() : '-'}
            </div>
          </div>
          {timer.showDiscountGroups && (
            <div className={`stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`} style={getBlockStyle(fullStyles.stats)}>
              <div className="stat-label">{t('timer.full.discountGroups', '優惠組數')}</div>
              <div className="stat-value">
                {timer.discountGroups ? parseInt(timer.discountGroups).toLocaleString() : '-'}
              </div>
            </div>
          )}
          <div className={`stat-box ${getFontSizeClass(fullStyles.stats?.fontSize)}`} style={getBlockStyle(fullStyles.stats)}>
            <div className="stat-label">{t('timer.full.playersCount', '目前人數/報名總數')}</div>
            <div className="stat-value">
              {timer.remainingPlayers || '-'} / {timer.totalGroups || '-'}
            </div>
          </div>
          <div className="qr-section">
            <div className="section-title">備註QR</div>
          </div>
        </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

}

export default TimerEmbed;
